/**
 * The legacy GSA START import (`data/gsa_assessment_import.json`, the shape the
 * old Assessments area seeded from) → a `releve.level-up/v1` package
 * (docs/LEVEL_UP.md). Pure: no I/O. The result is NOT validated here — the
 * caller parses it with `levelUpPackageSchema` exactly once, like any other
 * package.
 *
 * Raw shape: `template` (name + `metadata.tier_names`), `topics[]` (one per
 * metric: `topic_number`, `category`, `subcategory`, `name`, `description`,
 * `sort_order`; `max_tiers` / `total_points` are dropped) and a flat
 * `questions[]` (one per tier) joined on `topic_number`.
 *
 * Rules:
 *   - assessment key `gsa-start`; tier "0" is renamed "None apply" (its label stays `N/A`)
 *   - one pillar per `category`, in order of first appearance; the three GSA
 *     pillars get the wireframe's colour + icon (other categories get none)
 *   - metric key = slug of `name`, number = `topic_number`, ordered by `sort_order`
 *   - a tier whose statement starts "There are no requirements for this Tier" is `isEmpty`
 *   - `compliance_mappings.requirements` strings split on their first ': ' →
 *     `Green Ribbon Pillar` / `Impact Value Chain`; `compliance_mappings.frameworks` dropped
 *   - metric links come from `learning_content.resources` (identical on every tier):
 *     a text item titles the next URL; the "See guide: <url>" URL is titled "Guide"
 *
 * Warnings are {@link LevelUpLintItem}s: the two conversion-only codes
 * (`POINTS_NOT_D_PLUS_I`, `UNKNOWN_FRAMEWORK`) raised while reading the raw
 * file, then `lintLevelUpPackage` of the result.
 */
import { z } from "zod";
import { DEFAULT_BADGE_BANDS } from "./scoring.js";
import { lintLevelUpPackage } from "./lint.js";
import { LEVEL_UP_KEY_MAX, LEVEL_UP_PACKAGE_FORMAT, LEVEL_UP_TIER_KEYS, } from "./package.js";
export const GSA_START_ASSESSMENT_KEY = "gsa-start";
/** GSA START's three pillars get the wireframe's colours and icons on import (keyed by pillar key). */
const GSA_PILLAR_STYLES = {
    "educational-program": { color: "#e0744f", icon: "GraduationCap" },
    "organizational-culture": { color: "#15a267", icon: "Users" },
    "physical-place": { color: "#c99f0c", icon: "Home" },
};
/** The plan's term for tier 0 (the legacy data calls it "Not Applicable"). */
export const LEVEL_UP_TIER_ZERO_NAME = "None apply";
const EMPTY_TIER_PREFIX = "There are no requirements for this Tier";
const GREEN_RIBBON_PREFIX = "Green Ribbon Pillar";
const IMPACT_VALUE_CHAIN_PREFIX = "Impact Value Chain";
const GUIDE_TITLE = "Guide";
// ---------------------------------------------------------------------------
// Raw shape (lenient: only what the conversion reads)
// ---------------------------------------------------------------------------
const rawTopicSchema = z.object({
    topic_number: z.number().int(),
    category: z.string(),
    subcategory: z.string(),
    name: z.string(),
    description: z.string(),
    sort_order: z.number(),
});
const rawQuestionSchema = z.object({
    topic_number: z.number().int(),
    tier_level: z.number().int(),
    tier_name: z.string(),
    tier_label: z.string(),
    question_text: z.string(),
    point_value: z.number(),
    difficulty_rating: z.number(),
    impact_rating: z.number(),
    cost_rating: z.number(),
    evidence_required: z.boolean(),
    learning_content: z
        .object({
        explanation: z.string().nullish(),
        resources: z.array(z.string()).nullish(),
    })
        .nullish(),
    compliance_mappings: z
        .object({
        requirements: z.array(z.string()).nullish(),
    })
        .nullish(),
});
const rawImportSchema = z.object({
    template: z.object({
        name: z.string(),
        metadata: z.object({
            tier_names: z.record(z.enum(LEVEL_UP_TIER_KEYS), z.string()),
        }),
    }),
    topics: z.array(rawTopicSchema),
    questions: z.array(rawQuestionSchema),
});
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/** Lowercase ASCII slug: diacritics dropped, runs of anything else → one hyphen. */
export function levelUpSlug(value) {
    return value
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, LEVEL_UP_KEY_MAX)
        .replace(/-+$/g, "");
}
const isUrl = (value) => /^https?:\/\//i.test(value);
const guideUrlOf = (explanation) => {
    const match = /See guide:\s*(\S+)/.exec(explanation ?? "");
    return match ? match[1] : null;
};
// ---------------------------------------------------------------------------
// Conversion
// ---------------------------------------------------------------------------
export function convertLegacyGsaImport(raw) {
    const data = rawImportSchema.parse(raw);
    // Conversion-only items; the package lint is appended at the end.
    const warnings = [];
    // Tier names: the template's, with tier 0 renamed.
    const tierNames = {};
    for (const level of LEVEL_UP_TIER_KEYS) {
        const name = data.template.metadata.tier_names[level];
        if (name !== undefined)
            tierNames[level] = { en: level === "0" ? LEVEL_UP_TIER_ZERO_NAME : name };
    }
    const topics = [...data.topics].sort((a, b) => a.sort_order - b.sort_order);
    // Pillars: one per category, in order of first appearance.
    const pillars = [];
    for (const topic of topics) {
        const key = levelUpSlug(topic.category);
        if (!pillars.some((p) => p.key === key))
            pillars.push({ key, name: { en: topic.category }, ...GSA_PILLAR_STYLES[key] });
    }
    const questionsByTopic = new Map();
    for (const q of data.questions) {
        const list = questionsByTopic.get(q.topic_number) ?? [];
        list.push(q);
        questionsByTopic.set(q.topic_number, list);
    }
    const metrics = topics.map((topic) => {
        const metricKey = levelUpSlug(topic.name);
        const questions = [...(questionsByTopic.get(topic.topic_number) ?? [])].sort((a, b) => a.tier_level - b.tier_level);
        const tiers = questions.map((q) => {
            const tierLevel = q.tier_level;
            const isEmpty = q.question_text.startsWith(EMPTY_TIER_PREFIX);
            // Points become D + I; a file whose point_value disagrees is flagged.
            if (q.point_value !== q.difficulty_rating + q.impact_rating) {
                warnings.push({ code: "POINTS_NOT_D_PLUS_I", severity: "warning", metricKey, tierLevel });
            }
            const frameworks = { greenRibbonPillars: [], impactValueChain: [] };
            for (const requirement of q.compliance_mappings?.requirements ?? []) {
                const at = requirement.indexOf(": ");
                const prefix = at < 0 ? requirement : requirement.slice(0, at);
                const value = at < 0 ? "" : requirement.slice(at + 2).trim();
                const target = prefix === GREEN_RIBBON_PREFIX
                    ? frameworks.greenRibbonPillars
                    : prefix === IMPACT_VALUE_CHAIN_PREFIX
                        ? frameworks.impactValueChain
                        : null;
                if (!target || value === "") {
                    warnings.push({ code: "UNKNOWN_FRAMEWORK", severity: "warning", metricKey, tierLevel });
                    continue;
                }
                if (!target.includes(value))
                    target.push(value);
            }
            return {
                level: tierLevel,
                name: { en: tierLevel === 0 ? LEVEL_UP_TIER_ZERO_NAME : q.tier_name },
                label: { en: q.tier_label },
                statement: { en: q.question_text },
                isEmpty,
                difficulty: q.difficulty_rating,
                impact: q.impact_rating,
                cost: q.cost_rating,
                howToLinks: [],
                frameworks,
                evidenceRequired: q.evidence_required,
            };
        });
        // Links: the learning content is identical on every tier; read the first.
        const learning = questions[0]?.learning_content;
        const guideUrl = guideUrlOf(learning?.explanation);
        const links = [];
        let pendingTitle;
        for (const item of learning?.resources ?? []) {
            const text = item.trim();
            if (text === "")
                continue;
            if (!isUrl(text)) {
                pendingTitle = text;
                continue;
            }
            const title = text === guideUrl ? GUIDE_TITLE : pendingTitle;
            pendingTitle = undefined;
            links.push(title === undefined ? { url: text } : { title, url: text });
        }
        return {
            key: metricKey,
            pillarKey: levelUpSlug(topic.category),
            number: topic.topic_number,
            subcategory: { en: topic.subcategory },
            focus: { en: topic.name },
            description: { en: topic.description },
            links,
            tiers,
        };
    });
    const pkg = {
        format: LEVEL_UP_PACKAGE_FORMAT,
        assessment: { key: GSA_START_ASSESSMENT_KEY, name: { en: data.template.name } },
        metadata: { tierNames, badgeBands: [...DEFAULT_BADGE_BANDS], helpLinks: [] },
        pillars,
        metrics,
    };
    return { package: pkg, warnings: [...warnings, ...lintLevelUpPackage(pkg)] };
}
//# sourceMappingURL=legacyAdapter.js.map